import { computed, watch } from 'vue'
import { useI18n as useVueI18n } from 'vue-i18n'
import {
  setLocale as setAppLocale,
  SUPPORTED_LOCALES,
  getLocaleDisplayName,
  isLocaleSupported,
  type SupportedLocale,
} from '@/plugins/i18n'
import type { MessageSchema } from '@/types/i18n.d'

/**
 * Options for the useLocale composable
 */
export interface UseLocaleOptions {
  /**
   * Watch for locale changes and react
   */
  watchLocaleChange?: boolean
}

/**
 * Locale option for dropdowns/selects
 */
export interface LocaleOption {
  value: SupportedLocale
  label: string
}

/**
 * Composable for internationalization with typed keys
 *
 * @example
 * ```typescript
 * const { t, locale, setLocale, availableLocales } = useLocale()
 *
 * // Type-safe translations
 * const title = t('auth.login.title')
 *
 * // With parameters
 * const error = t('errors.validation.field_too_short', { min_length: 12 })
 *
 * // Change locale
 * await setLocale('pl')
 * ```
 */
export function useLocale(options: UseLocaleOptions = {}) {
  const { watchLocaleChange = false } = options
  const vueI18n = useVueI18n<{ message: MessageSchema }>()

  /**
   * Current locale - reactive to vue-i18n locale changes
   */
  const locale = computed(() => vueI18n.locale.value as SupportedLocale)

  /**
   * Available locales
   */
  const availableLocales = computed(() => [...SUPPORTED_LOCALES])

  /**
   * Locale options for dropdowns/selects
   */
  const localeOptions = computed<LocaleOption[]>(() =>
    SUPPORTED_LOCALES.map((loc) => ({
      value: loc,
      label: getLocaleDisplayName(loc),
    }))
  )

  /**
   * Set the current locale
   */
  async function setLocale(newLocale: SupportedLocale): Promise<void> {
    if (!isLocaleSupported(newLocale)) {
      console.warn(`[useLocale] Locale not supported: ${newLocale}`)
      return
    }
    await setAppLocale(newLocale)
  }

  /**
   * Translate a key with optional parameters
   * Wrapper around vue-i18n's t function
   */
  function t(key: string, params?: Record<string, unknown>): string {
    // Using vue-i18n's t function directly
    if (params) {
      return vueI18n.t(key, params)
    }
    return vueI18n.t(key)
  }

  /**
   * Check if a translation key exists
   */
  function te(key: string): boolean {
    return vueI18n.te(key)
  }

  /**
   * Translate an API error code to a user-friendly message
   * Falls back to the provided message if no translation exists
   */
  function translateError(errorCode: string, fallbackMessage: string, context?: Record<string, unknown>): string {
    // Convert dot notation to underscore for errors namespace
    // e.g., "auth.invalid_credentials" -> "errors.auth.invalid_credentials"
    const translationKey = `errors.${errorCode.replace(/\./g, '.')}`

    if (te(translationKey)) {
      return t(translationKey, context)
    }

    // Try without the errors prefix (in case the code is already a translation key)
    if (te(errorCode)) {
      return t(errorCode, context)
    }

    return fallbackMessage
  }

  /**
   * Get display name for a locale
   */
  function getLocaleName(loc: SupportedLocale): string {
    return getLocaleDisplayName(loc)
  }

  // Watch for external locale changes if requested
  if (watchLocaleChange) {
    watch(
      () => vueI18n.locale.value,
      (newLocale) => {
        document.documentElement.setAttribute('lang', newLocale)
      }
    )
  }

  return {
    // Translation functions
    t,
    te,
    translateError,

    // Locale management
    locale,
    setLocale,
    availableLocales,
    localeOptions,
    getLocaleName,

    // Raw vue-i18n access for advanced usage
    i18n: vueI18n,
  }
}

export type { SupportedLocale }
